import { useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  FormHelperText,
  Heading,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  Stepper,
  Textarea,
  VStack,
  Text,
  useSteps,
  useToast,
  useColorModeValue,
  SimpleGrid,
  Badge,
  Link,
  Radio,
  RadioGroup,
  Spinner,
} from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { ImageUploader } from './ImageUploader';
import { useStorage } from '../../hooks/useStorage';
import { createBounty } from '../../hooks/useBounties';
import { useUser } from '../../contexts/UserContext';
import { useAuth } from '../../contexts/AuthContext';
import { useStripeConfig, callCreatePaymentIntent, getPaymentErrorMessage } from '../../hooks/useStripe';
import { usePaymentSettings } from '../../hooks/useSettings';
import CardForm from '../payments/CardForm';
import { RESEARCH_TYPES } from '../../utils/formatters';
import { Price } from '../common/Price';

const steps = [
  { title: 'Details', description: 'Task information' },
  { title: 'Images', description: 'Upload artwork photos' },
  { title: 'Review', description: 'Confirm and submit' },
];

export default function CreateBounty() {
  const { activeStep, goToNext, goToPrevious } = useSteps({ index: 0, count: steps.length });
  const { currentUser } = useAuth();
  const { userData } = useUser();
  const { uploadFiles, uploading, progress } = useStorage();
  const navigate = useNavigate();
  const toast = useToast();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [researchType, setResearchType] = useState('');
  const [rewardAmount, setRewardAmount] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const { stripePromise, loading: stripeLoading } = useStripeConfig();
  const { feePercent } = usePaymentSettings();
  const getPaymentMethodRef = useRef(null);
  const savedCardsRef = useRef(userData?.stripePaymentMethods || []);
  const savedCards = savedCardsRef.current;
  const [cardChoice, setCardChoice] = useState(savedCards.length > 0 ? savedCards[0].id : 'new');

  const handleCardFormReady = useCallback((getPaymentMethod) => {
    getPaymentMethodRef.current = getPaymentMethod;
  }, []);

  const cardBg = useColorModeValue('white', 'gray.800');
  const reviewBg = useColorModeValue('gray.50', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const stepDescColor = useColorModeValue('gray.500', 'gray.400');

  const isStep1Valid = title.trim().length >= 5 && description.trim().length >= 20 && researchType && rewardAmount >= 100;

  const isAdmin = userData?.isAdmin === true;

  const handleSubmit = async () => {
    if (!currentUser || !userData) return;
    setSubmitting(true);

    try {
      const bountyAmountCents = Math.round(parseFloat(rewardAmount) * 100);
      let holdResult = { paymentIntentId: null, holdAmount: 0, paymentMethodId: null };

      if (!isAdmin) {
        // 1. Get payment method (saved or new card)
        let paymentMethodId;
        if (cardChoice !== 'new') {
          paymentMethodId = cardChoice;
        } else {
          if (!getPaymentMethodRef.current) {
            throw new Error('Card form is not ready. Please wait a moment and try again.');
          }
          paymentMethodId = await getPaymentMethodRef.current();
        }

        // 2. Create payment intent (10% hold)
        holdResult = await callCreatePaymentIntent(paymentMethodId, bountyAmountCents);
      }

      // 3. Upload images
      let imageUrls = [];
      if (imageFiles.length > 0) {
        const tempBountyId = `temp_${Date.now()}`;
        imageUrls = await uploadFiles(imageFiles, `bounties/${tempBountyId}`);
      }

      // 4. Create bounty with stripe hold info
      const bountyId = await createBounty(
        {
          title: title.trim(),
          description: description.trim(),
          researchType,
          rewardAmount: bountyAmountCents,
          imageUrls,
          thumbnailUrl: imageUrls[0] || '',
          stripeHoldPaymentIntentId: holdResult.paymentIntentId || null,
          holdAmount: holdResult.holdAmount || 0,
          stripePaymentMethodId: holdResult.paymentMethodId || null,
        },
        currentUser.uid,
        userData.isFullyAnonymous ? 'Anonymous' : userData.anonymizedUsername
      );

      toast({
        title: 'Task posted!',
        description: 'Your research task is now live.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      navigate(`/tasks/${bountyId}`);
    } catch (error) {
      const msg = getPaymentErrorMessage(error);
      toast({
        title: 'Payment failed',
        description: msg,
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
      setSubmitting(false);
    }
  };

  return (
    <Container maxW="2xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Box>
          <Heading size="lg" color={headingColor}>Post a Research Task</Heading>
          <Text color="gray.600" mt={1}>
            Offer a reward for art historical research
          </Text>
        </Box>

        <VStack spacing={2} align="stretch">
          <Stepper index={activeStep} colorScheme="orange">
            {steps.map((step, index) => (
              <Step key={index} flex="1">
                <StepIndicator>
                  <StepStatus
                    complete={<StepIcon />}
                    incomplete={<StepNumber />}
                    active={<StepNumber />}
                  />
                </StepIndicator>
                <StepSeparator />
              </Step>
            ))}
          </Stepper>
          <SimpleGrid columns={3}>
            {steps.map((step, index) => (
              <Box
                key={index}
                textAlign={index === 0 ? 'left' : index === 1 ? 'center' : 'right'}
              >
                <Text fontSize="sm" fontWeight="medium" color={headingColor}>{step.title}</Text>
                <Text fontSize="xs" color={stepDescColor}>{step.description}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </VStack>

        <Box bg={cardBg} rounded="xl" p={{ base: 4, md: 8 }} boxShadow="sm" borderWidth={1} borderColor={useColorModeValue('gray.100', 'gray.700')}>
          {/* Step 1: Details */}
          {activeStep === 0 && (
            <VStack spacing={6} align="stretch">
              <FormControl isRequired>
                <FormLabel>Task Title</FormLabel>
                <Input
                  placeholder="e.g. Attribution for 17th century oil portrait"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  size="lg"
                />
                <FormHelperText>{title.length}/100 characters (min 5)</FormHelperText>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Research Type</FormLabel>
                <Select
                  placeholder="Select research type"
                  value={researchType}
                  onChange={(e) => setResearchType(e.target.value)}
                  size="lg"
                >
                  {Object.entries(RESEARCH_TYPES).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Description</FormLabel>
                <Textarea
                  placeholder="Describe what you know about the artwork, what research is needed, and what evidence would be most helpful..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  resize="vertical"
                />
                <FormHelperText>{description.length} characters (min 20)</FormHelperText>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Reward Amount (USD)</FormLabel>
                <NumberInput
                  min={100}
                  max={50000}
                  value={rewardAmount}
                  onChange={(val) => setRewardAmount(val)}
                  size="lg"
                >
                  <NumberInputField placeholder="$250" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>Minimum $100</FormHelperText>
              </FormControl>
            </VStack>
          )}

          {/* Step 2: Images */}
          {activeStep === 1 && (
            <VStack spacing={6} align="stretch">
              <Box>
                <Heading size="sm" mb={1}>Upload Artwork Images</Heading>
                <Text color="gray.500" fontSize="sm">
                  At least one image is required. The first image will be the cover.
                </Text>
              </Box>
              <ImageUploader onFilesChange={setImageFiles} />
              {uploading && (
                <Box>
                  <Text fontSize="sm" mb={1}>Uploading... {progress}%</Text>
                </Box>
              )}
            </VStack>
          )}

          {/* Step 3: Review */}
          {activeStep === 2 && (
            <VStack spacing={6} align="stretch">
              <Heading size="sm">Review Your Task</Heading>

              <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4}>
                <Box bg={reviewBg} rounded="lg" p={4}>
                  <Text fontSize="xs" color="gray.500" mb={1}>TITLE</Text>
                  <Text fontWeight="medium">{title}</Text>
                </Box>
                <Box bg={reviewBg} rounded="lg" p={4}>
                  <Text fontSize="xs" color="gray.500" mb={1}>RESEARCH TYPE</Text>
                  <Badge colorScheme={RESEARCH_TYPES[researchType]?.color}>
                    {RESEARCH_TYPES[researchType]?.label}
                  </Badge>
                </Box>
                <Box bg={reviewBg} rounded="lg" p={4}>
                  <Text fontSize="xs" color="gray.500" mb={1}>REWARD</Text>
                  <Text fontWeight="bold" fontSize="xl" color="green.600">
                    <Price cents={parseFloat(rewardAmount) * 100} />
                  </Text>
                </Box>
                <Box bg={reviewBg} rounded="lg" p={4}>
                  <Text fontSize="xs" color="gray.500" mb={1}>IMAGES</Text>
                  <Text fontWeight="medium">{imageFiles.length} image(s)</Text>
                </Box>
              </SimpleGrid>

              <Box bg={reviewBg} rounded="lg" p={4}>
                <Text fontSize="xs" color="gray.500" mb={1}>DESCRIPTION</Text>
                <Text fontSize="sm" noOfLines={5}>{description}</Text>
              </Box>

              <Box bg="orange.50" rounded="lg" p={4}>
                <Text fontSize="sm" color="orange.800">
                  <strong>Note:</strong> Your identity will remain {userData?.isFullyAnonymous ? 'anonymous' : 'pseudonymous'}. This task will be posted under {userData?.isFullyAnonymous ? <>the name <strong>Anonymous</strong></> : <>your username <strong>{userData?.anonymizedUsername}</strong></>}.
                </Text>
              </Box>

              {/* Payment section — hidden for admins */}
              {!isAdmin && (
                <Box>
                  <Heading size="sm" mb={3}>Payment Method</Heading>
                  <Text fontSize="sm" color="gray.600" mb={4}>
                    A hold of <strong><Price cents={Math.max(Math.round(parseFloat(rewardAmount || 0) * 10), 50)} /></strong> (10%) will be placed to verify your payment method.
                    When your task is completed, you'll be charged <strong><Price cents={parseFloat(rewardAmount || 0) * 100} /></strong> + a <strong>{feePercent}%</strong> platform fee.
                  </Text>

                  {savedCards.length > 0 && (
                    <RadioGroup value={cardChoice} onChange={setCardChoice} mb={4}>
                      <VStack align="stretch" spacing={2}>
                        {savedCards.map((card) => (
                          <Radio key={card.id} value={card.id} colorScheme="orange">
                            <Text fontSize="sm">
                              <strong>{card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : 'Card'}</strong> ending in <strong>{card.last4}</strong>
                            </Text>
                          </Radio>
                        ))}
                        <Radio value="new" colorScheme="orange">
                          <Text fontSize="sm">Enter a new card</Text>
                        </Radio>
                      </VStack>
                    </RadioGroup>
                  )}

                  {(savedCards.length === 0 || cardChoice === 'new') && (
                    stripeLoading ? (
                      <HStack spacing={2} py={4} justify="center">
                        <Spinner size="sm" color="orange.500" />
                        <Text fontSize="sm" color="gray.500">Loading payment form...</Text>
                      </HStack>
                    ) : stripePromise ? (
                      <Elements stripe={stripePromise}>
                        <CardForm onReady={handleCardFormReady} />
                      </Elements>
                    ) : (
                      <Text fontSize="sm" color="red.500">
                        Unable to load payment form. Please refresh the page.
                      </Text>
                    )
                  )}
                </Box>
              )}

              <Text fontSize="xs" color="gray.500" textAlign="center">
                By posting this task, you confirm that you accept the{' '}
                <Link as={RouterLink} to="/terms" color="orange.400" target="_blank">Terms of Service</Link>
                ,{' '}<Link as={RouterLink} to="/disputes" color="orange.400" target="_blank">Dispute Resolution Policy</Link>
                {' '}and have read the{' '}
                <Link as={RouterLink} to="/privacy" color="orange.400" target="_blank">Privacy Policy</Link>.
              </Text>
            </VStack>
          )}
        </Box>

        <HStack justify={activeStep > 0 ? 'space-between' : 'flex-end'}>
          {activeStep > 0 && (
            <Button onClick={goToPrevious} variant="ghost" size="lg">
              Back
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button
              onClick={goToNext}
              isDisabled={
                (activeStep === 0 && !isStep1Valid) ||
                (activeStep === 1 && imageFiles.length === 0)
              }
              colorScheme="orange"
              size="lg"
            >
              Continue
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              isLoading={submitting || uploading}
              loadingText={uploading ? `Uploading... ${progress}%` : 'Processing payment...'}
              colorScheme="orange"
              size="lg"
            >
              Post Task
            </Button>
          )}
        </HStack>
      </VStack>
    </Container>
  );
}
